import { useEffect, useState } from 'react';
import { useApiClient } from '@/lib/api';
import { fetchSignatureTopography } from '@/lib/case_signatures';

export interface SignatureTopographyState {
  /** Object URL do PNG derivado, pronto para `<img src>`. */
  url: string | null;
  /** true = backend respondeu 422 (imagem sem base para o mapa) — estado
   * "indisponível" honesto, distinto de loading/erro (ADR-0036 §2.E). */
  unavailable: boolean;
  /** true = falha técnica (rede/autenticação/5xx) — NUNCA apresentada como
   * "indisponível" (classe F-E26.F3-02, corrigida aqui na E26.F4.T05). */
  error: boolean;
  loading: boolean;
}

const IDLE: SignatureTopographyState = {
  url: null,
  unavailable: false,
  error: false,
  loading: false,
};

/**
 * Fetch autenticado da topografia de pressão estimada (E26.F1/ADR-0036).
 * Espelha `useSignatureBlob` (object URL + revoke no cleanup), com o estado
 * extra `unavailable` para a UI exibir indisponibilidade sem fingir mapa.
 */
export function useSignatureTopography(
  caseId: string,
  signatureId: string | undefined,
): SignatureTopographyState {
  const client = useApiClient();
  const [state, setState] = useState<SignatureTopographyState>(IDLE);

  useEffect(() => {
    if (!signatureId || !caseId) {
      setState(IDLE);
      return;
    }
    const resolvedSignatureId = signatureId;
    let cancelled = false;
    let created: string | null = null;
    setState({ url: null, unavailable: false, error: false, loading: true });
    async function load() {
      try {
        const blob = await fetchSignatureTopography(client, caseId, resolvedSignatureId);
        if (cancelled) return;
        if (blob === null) {
          setState({ url: null, unavailable: true, error: false, loading: false });
          return;
        }
        created = URL.createObjectURL(blob);
        setState({ url: created, unavailable: false, error: false, loading: false });
      } catch {
        if (!cancelled) setState({ url: null, unavailable: false, error: true, loading: false });
      }
    }
    void load();
    return () => {
      cancelled = true;
      if (created !== null) URL.revokeObjectURL(created);
    };
  }, [client, caseId, signatureId]);

  return state;
}
